/**
 * Small shared pieces for the Absence Compliance screens.
 */
import React, { useEffect, useState } from "react";
import Rodal from "rodal";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "rodal/lib/rodal.css";
import "react-toastify/dist/ReactToastify.css";
import { toast } from "react-toastify";
import { getCookieData, getPermissionStatus } from "../../utils/CommonFunction";
import {
  AWARENESS_TEXT,
  buildNudgeMessage,
  buildShareLinks,
  copyText,
  describeDeadline,
  nudgeDays,
  statusColor,
  statusLabel,
} from "./pendingAbsenceHelpers";

export function PageHeading({ title, children }) {
  return (
    <div className="row wrapper border-bottom white-bg page-heading">
      <div className="col-lg-12">
        <h2>{title}</h2>
        {children}
      </div>
    </div>
  );
}

export function StatusBadge({ status }) {
  return (
    <span
      className="label"
      style={{
        backgroundColor: statusColor(status),
        color: "#ffffff",
        whiteSpace: "nowrap",
      }}
    >
      {statusLabel(status)}
    </span>
  );
}

const TONE_COLOR = { ok: "#676a6c", soon: "#e67e22", overdue: "#b03a2e" };

export function DeadlineLabel({ item }) {
  const { text, tone } = describeDeadline(item);
  if (!text) return <span className="text-muted">-</span>;
  return (
    <span style={{ color: TONE_COLOR[tone], fontWeight: tone === "ok" ? 400 : 600 }}>
      {text}
    </span>
  );
}

/** English + Myanmar awareness text, and the live count, shown above the employee's list. */
export function AwarenessBanner({ pendingCount }) {
  return (
    <div className="alert alert-warning" role="alert" style={{ marginBottom: 15 }}>
      {typeof pendingCount === "number" && (
        <p style={{ fontSize: 15, marginBottom: 8 }}>
          <strong>
            You have {pendingCount} pending absence{pendingCount === 1 ? "" : "s"} to explain.
          </strong>
        </p>
      )}
      <p style={{ marginBottom: 8 }}>{AWARENESS_TEXT.en}</p>
      <p style={{ marginBottom: 0, lineHeight: 1.8 }}>{AWARENESS_TEXT.my}</p>
    </div>
  );
}

const pad = (number) => String(number).padStart(2, "0");

/** 'YYYY-MM-DD' -> local Date (null if it is not a complete date). */
function toDate(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "");
  return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null;
}

/** Date -> 'YYYY-MM-DD' ('' for no date). */
function toText(date) {
  return date ? `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` : "";
}

/**
 * Date field that always shows and accepts YYYY-MM-DD. The browser's own <input type="date"> shows
 * the date in the computer's regional format, which cannot be changed from the page.
 * `value` and `onChange` use 'YYYY-MM-DD' strings ('' when empty).
 */
export function DateInput({ value, onChange, disabled = false, className = "form-control", ariaLabel }) {
  return (
    <ReactDatePicker
      selected={toDate(value)}
      onChange={(date) => onChange(toText(date))}
      dateFormat="yyyy-MM-dd"
      placeholderText="YYYY-MM-DD"
      className={className}
      wrapperClassName="pa-date-input"
      popperClassName="pa-calendar"
      showMonthDropdown
      showYearDropdown
      dropdownMode="select"
      yearDropdownItemNumber={100}
      scrollableYearDropdown
      showPopperArrow={false}
      disabled={disabled}
      isClearable={!disabled && Boolean(value)}
      autoComplete="off"
      portalId="pa-date-portal"
      ariaLabelledBy={ariaLabel}
    />
  );
}

/**
 * A confirm dialog with a form body. Rodal needs a fixed height unless it is overridden.
 */
export function ActionDialog({
  visible,
  title,
  onClose,
  onConfirm,
  confirmLabel = "Submit",
  confirmClassName = "btn btn-primary",
  busy = false,
  confirmDisabled = false,
  width = 520,
  children,
}) {
  return (
    <Rodal
      visible={visible}
      onClose={busy ? () => {} : onClose}
      width={width}
      height={400}
      customStyles={{ height: "auto", bottom: "auto", top: "8%", overflow: "auto", maxHeight: "84%" }}
      closeOnEsc={!busy}
    >
      <div style={{ padding: "5px 5px 0 5px" }}>
        <h3 style={{ marginTop: 0, marginBottom: 15 }}>{title}</h3>
        {children}
        <div style={{ marginTop: 15, textAlign: "right" }}>
          <button
            type="button"
            className="btn btn-white"
            onClick={onClose}
            disabled={busy}
            style={{ marginRight: 8 }}
          >
            Cancel
          </button>
          <button
            type="button"
            className={confirmClassName}
            onClick={onConfirm}
            disabled={busy || confirmDisabled}
          >
            {busy ? "Please wait..." : confirmLabel}
          </button>
        </div>
      </div>
    </Rodal>
  );
}

/**
 * "Send info" for one employee: shows the text and opens Telegram / Viber with it ready, or
 * copies it. The sender picks the recipient in the chat app; HRM sends nothing itself.
 * `items` is the list on screen; the reminder covers that employee's days that still wait for them.
 */
export function ShareButton({ item, items }) {
  const [open, setOpen] = useState(false);
  const days = nudgeDays(items, item.user_id);
  const message = buildNudgeMessage(item.display_name, days);
  const links = buildShareLinks(message);

  const copy = async () => {
    const ok = await copyText(message);
    if (ok) toast.success("Text copied. Paste it into any chat.");
    else toast.error("Could not copy. Please select the text and copy it.");
  };

  return (
    <>
      <button
        type="button"
        className="btn btn-white btn-xs"
        disabled={days.length === 0}
        title={days.length === 0 ? "No listed day is waiting for this employee." : "Send this employee's pending absence info"}
        onClick={() => setOpen(true)}
      >
        Send info
      </button>
      {/* mounted only while open: a table row would otherwise carry a hidden dialog each */}
      {open && (
      <ActionDialog
        visible
        title={`Send info: ${item.display_name}`}
        confirmLabel="Close"
        onClose={() => setOpen(false)}
        onConfirm={() => setOpen(false)}
      >
        <p className="text-muted">
          This text is sent through the app you choose. You pick who receives it (a person or a
          group); nothing is sent from HRM.
        </p>
        <textarea
          className="form-control"
          rows={5}
          readOnly
          value={message}
          data-testid="share-message"
        />
        <div style={{ marginTop: 12 }}>
          <a className="btn btn-primary btn-sm" style={{ marginRight: 8 }} href={links.telegram} target="_blank" rel="noopener noreferrer">
            Telegram
          </a>
          <a className="btn btn-primary btn-sm" style={{ marginRight: 8 }} href={links.viber}>
            Viber
          </a>
          <button type="button" className="btn btn-white btn-sm" onClick={copy}>
            Copy text
          </button>
        </div>
      </ActionDialog>
      )}
    </>
  );
}

/**
 * The page-level permission of the logged-in designation (tbl_permission_approval).
 * Returns { loaded, isView, isAddNew, isEdit }. The backend enforces the same permission.
 */
export function usePagePermission(title, type = "Attendance") {
  const [permission, setPermission] = useState({
    loaded: false,
    isView: 0,
    isAddNew: 0,
    isEdit: 0,
  });

  useEffect(() => {
    let cancelled = false;
    const user = getCookieData("user_info");
    getPermissionStatus(user && user.designations_id, title, type).then(
      (status) => {
        if (!cancelled) setPermission({ loaded: true, ...status });
      }
    );
    return () => {
      cancelled = true;
    };
  }, [title, type]);

  return permission;
}

export function NoPermission() {
  return (
    <div className="wrapper wrapper-content">
      <div className="alert alert-danger">
        You do not have permission to use this screen.
      </div>
    </div>
  );
}

/** Prev / Next pager for server-paged lists. */
export function Pager({ page, pageSize, total, onChange }) {
  const lastPage = Math.max(Math.ceil(total / pageSize), 1);
  return (
    <div style={{ marginTop: 10, display: "flex", alignItems: "center" }}>
      <button
        type="button"
        className="btn btn-white btn-sm"
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
      >
        Previous
      </button>
      <span style={{ margin: "0 12px" }}>
        Page {page} of {lastPage} ({total} record{total === 1 ? "" : "s"})
      </span>
      <button
        type="button"
        className="btn btn-white btn-sm"
        disabled={page >= lastPage}
        onClick={() => onChange(page + 1)}
      >
        Next
      </button>
    </div>
  );
}
